import { http } from '../api/http';

export type BankAccountType = 'CLABE' | 'ACCOUNT_NUMBER' | 'DEBIT';
export type BankAccountStatus = 'PENDING' | 'VERIFIED' | 'REJECTED' | 'DISABLED';

export interface Bank {
  id: string;
  code: string;
  name: string;
  country: string;
}

export interface BankAccount {
  id: string;
  bankId: string;
  bankName: string;
  bankCode: string;
  accountType: BankAccountType;
  status: BankAccountStatus;
  beneficiaryName: string;
  rfc?: string;
  clabe?: string;
  accountNumber?: string;
  cardLast4?: string;
  currency: string;
  isDefault: boolean;
  verifiedAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  documentsCount?: number;
}

export interface CreateBankAccountDto {
  bankId: string;
  beneficiaryName: string;
  rfc?: string;
  clabe?: string;
  accountNumber?: string;
  accountType: BankAccountType;
  notes?: string;
  isDefault?: boolean;
  mfaToken?: string; // MFA token for verification
}

export interface UpdateBankAccountDto {
  beneficiaryName?: string;
  rfc?: string;
  clabe?: string;
  accountNumber?: string;
  notes?: string;
  isDefault?: boolean;
  status?: BankAccountStatus;
  mfaToken?: string; // MFA token for verification
}

// Mantengo la interfaz legacy para compatibilidad
export interface BankDetails {
  holder_name: string;
  bank_name: string;
  clabe: string;
  account_number?: string | null;
  rfc?: string | null;
  notes?: string | null;
  updated_at?: string | null;
}

export const BankService = {
  // Endpoints legacy (para mantener compatibilidad)
  getMyBankDetails(): Promise<BankDetails | null> {
    return this.getMyBankAccounts()
      .then(accounts => {
        const clabeDefault = accounts.find(
          acc => acc.accountType === 'CLABE' && acc.isDefault
        );
        const clabeAccount = clabeDefault || accounts.find(acc => acc.accountType === 'CLABE');
        const defaultAccount = clabeAccount || accounts.find(acc => acc.isDefault) || accounts[0];
        if (!defaultAccount) return null;
        
        return {
          holder_name: defaultAccount.beneficiaryName,
          bank_name: defaultAccount.bankName,
          clabe: defaultAccount.clabe || '',
          account_number: defaultAccount.accountNumber,
          rfc: defaultAccount.rfc,
          notes: defaultAccount.notes,
          updated_at: defaultAccount.updatedAt
        };
      })
      .catch(() => null);
  },

  updateMyBankDetails(payload: BankDetails, mfaToken: string): Promise<BankDetails> {
    // Convertir del formato legacy al nuevo formato
    return this.getMyBankAccounts().then(accounts => {
      const defaultAccount = accounts.find(acc => acc.isDefault) || accounts[0];
      
      if (defaultAccount) {
        // Actualizar cuenta existente
        return this.updateBankAccount(defaultAccount.id, {
          beneficiaryName: payload.holder_name,
          clabe: payload.clabe,
          accountNumber: payload.account_number || undefined,
          rfc: payload.rfc || undefined,
          notes: payload.notes || undefined,
          mfaToken
        }).then(updated => ({
          holder_name: updated.beneficiaryName,
          bank_name: updated.bankName,
          clabe: updated.clabe || '',
          account_number: updated.accountNumber,
          rfc: updated.rfc,
          notes: updated.notes,
          updated_at: updated.updatedAt
        }));
      } else {
        // Crear nueva cuenta si no existe ninguna
        // Necesitamos un bankId por defecto - asumimos el primero disponible
        return this.getBanks().then(banks => {
          const defaultBank = banks.find(b => b.name.includes(payload.bank_name)) || banks[0];
          
          return this.createBankAccount({
            bankId: defaultBank.id,
            beneficiaryName: payload.holder_name,
            clabe: payload.clabe,
            accountNumber: payload.account_number || undefined,
            rfc: payload.rfc || undefined,
            notes: payload.notes || undefined,
            accountType: 'CLABE',
            isDefault: true,
            mfaToken
          }).then(created => ({
            holder_name: created.beneficiaryName,
            bank_name: created.bankName,
            clabe: created.clabe || '',
            account_number: created.accountNumber,
            rfc: created.rfc,
            notes: created.notes,
            updated_at: created.updatedAt
          }));
        });
      }
    });
  },

  // Nuevos endpoints para el sistema bancario completo
  getBanks(): Promise<Bank[]> {
    return http<Bank[]>('/me/banks');
  },

  getMyBankAccounts(): Promise<BankAccount[]> {
    return http<BankAccount[]>('/me/bank-accounts');
  },

  getBankAccount(id: string): Promise<BankAccount> {
    return http<BankAccount>(`/me/bank-accounts/${id}`);
  },

  createBankAccount(payload: CreateBankAccountDto): Promise<BankAccount> {
    return http<BankAccount>('/me/bank-accounts', {
      method: 'POST',
      body: payload
    });
  },

  updateBankAccount(id: string, payload: UpdateBankAccountDto): Promise<BankAccount> {
    return http<BankAccount>(`/me/bank-accounts/${id}`, {
      method: 'PUT',
      body: payload
    });
  },

  deleteBankAccount(id: string): Promise<{ success: boolean }> {
    return http<{ success: boolean }>(`/me/bank-accounts/${id}`, {
      method: 'DELETE'
    });
  }
};
